/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Checkbox as InMarKitCheckbox,
  Collapse,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import isEmpty from 'lodash-es/isEmpty';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { getLineItemCloneWarnings } from '../../util/cloneWarnings';
import { isBlank, omitCents, round } from '../../util/helpers';
import { LineItem } from '../../validationSchemas/LineItemSchema';
import Checkbox from '../forms/Checkbox';
import CloneFieldWarnings from '../forms/CloneFieldWarnings';
import DatePicker from '../forms/DatePicker';
import NumericTextField from '../forms/NumericTextField';
import TextField from '../forms/TextField';

export interface DuplicateLineItemFieldsProps {
  lineItem: LineItem;
  fieldNamePrefix?: string;
}

function DuplicateLineItemFields({ lineItem, fieldNamePrefix = 'new_insertion_order' }: DuplicateLineItemFieldsProps) {
  const [isShowDetails, setShowDetails] = useState(false);
  const [duplicateAllAdGroups, setDuplicateAllAdGroups] = useState(true);
  const [duplicateAllLocations, setDuplicateAllLocations] = useState(true);
  const [duplicateAllCreatives, setDuplicateAllCreatives] = useState(true);

  const { control, getValues, setValue, trigger, watch } = useFormContext();
  const { fields: adGroups } = useFieldArray({
    control,
    name: `${fieldNamePrefix}.campaigns`,
  });
  const { fields: deliveryProfiles } = useFieldArray({
    control,
    name: `${fieldNamePrefix}.customDeliveryProfiles`,
  });
  const copyDeliveryProfiles = watch(`${fieldNamePrefix}.copyDeliveryProfiles`);

  // ADOPS-2722 — risky settings that will be carried over from the original.
  const cloneWarnings = getLineItemCloneWarnings(lineItem, fieldNamePrefix);

  // Live flight-start and delivery-profile values drive the date validations below.
  const lineItemStartDate = watch(`${fieldNamePrefix}.start_date`);
  const watchedProfiles = watch(`${fieldNamePrefix}.customDeliveryProfiles`);

  // True if `date` falls within another profile row's [start, end] range. Reads the live
  // values via getValues so it stays correct both when rendering the calendar (shouldDisableDate)
  // and when invoked later by react-hook-form's validate. Mirrors the overlap guard in
  // CustomDeliveryProfileModal so the same dates are blocked here.
  const isDateTakenByOtherRow = (rowIndex: number, date: Date) =>
    ((getValues(`${fieldNamePrefix}.customDeliveryProfiles`) as any[]) || []).some((p: any, i: number) => {
      if (i === rowIndex || !p?.startDate || !p?.endDate) return false;
      const start = startOfDay(new Date(p.startDate));
      const end = startOfDay(new Date(p.endDate));
      const target = startOfDay(date);
      return (
        (isAfter(target, start) || isSameDay(target, start)) && (isBefore(target, end) || isSameDay(target, end))
      );
    });

  // ----- react-hook-form validate rules for the profile date fields -----
  // These run on every validation pass (and via the trigger effect below), so an invalid
  // range — overlapping, out of order, before the flight start, or missing — surfaces as a
  // form error and disables the Duplicate button. All sibling values are read via getValues
  // at call time to avoid stale closures.
  const validateProfileStart = (index: number) => (value: any) => {
    if (!getValues(`${fieldNamePrefix}.copyDeliveryProfiles`)) return true;
    if (!value) return 'Start date is required';
    const start = startOfDay(new Date(value));
    const liStart = getValues(`${fieldNamePrefix}.start_date`);
    if (liStart && isBefore(start, startOfDay(new Date(liStart))))
      return 'Start date cannot be before the line item start date';
    const rowEnd = getValues(`${fieldNamePrefix}.customDeliveryProfiles.${index}.endDate`);
    if (rowEnd && isAfter(start, startOfDay(new Date(rowEnd)))) return 'Start date must be on or before the end date';
    if (isDateTakenByOtherRow(index, start)) return 'Date range overlaps another delivery period';
    return true;
  };

  const validateProfileEnd = (index: number) => (value: any) => {
    if (!getValues(`${fieldNamePrefix}.copyDeliveryProfiles`)) return true;
    if (!value) return 'End date is required';
    const end = startOfDay(new Date(value));
    const liStart = getValues(`${fieldNamePrefix}.start_date`);
    if (liStart && isBefore(end, startOfDay(new Date(liStart))))
      return 'End date cannot be before the line item start date';
    const rowStart = getValues(`${fieldNamePrefix}.customDeliveryProfiles.${index}.startDate`);
    if (rowStart && isBefore(end, startOfDay(new Date(rowStart)))) return 'End date must be on or after the start date';
    if (isDateTakenByOtherRow(index, end)) return 'Date range overlaps another delivery period';
    return true;
  };

  // Primitive fingerprint over every profile start/end so the effects below fire only when
  // the dates actually change — watch() returns a new array reference each render, which
  // would otherwise loop.
  const profilesDatesFingerprint = (watchedProfiles || [])
    .map((p: any) => `${p?.startDate?.valueOf?.() ?? p?.startDate}|${p?.endDate?.valueOf?.() ?? p?.endDate}`)
    .join(',');

  // Keep the new line item's end date in sync with the latest delivery-profile end date.
  useEffect(() => {
    if (!copyDeliveryProfiles) return;
    const endDates = (watchedProfiles || [])
      .map((p: any) => p?.endDate)
      .filter(Boolean)
      .map((d: any) => new Date(d));
    if (endDates.length === 0) return;
    const latest = endDates.reduce((a, b) => (a > b ? a : b));
    setValue(`${fieldNamePrefix}.end_date`, latest, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesDatesFingerprint, copyDeliveryProfiles]);

  // Re-validate every profile date field whenever any profile date or the flight start
  // changes. RHF only re-validates the edited field by default, but one row's change can
  // flip another row's overlap status, so we trigger the whole set.
  useEffect(() => {
    if (!copyDeliveryProfiles) return;
    const profiles = (getValues(`${fieldNamePrefix}.customDeliveryProfiles`) as any[]) || [];
    if (profiles.length === 0) return;
    const names = profiles.flatMap((_p, i) => [
      `${fieldNamePrefix}.customDeliveryProfiles.${i}.startDate`,
      `${fieldNamePrefix}.customDeliveryProfiles.${i}.endDate`,
    ]);
    trigger(names);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesDatesFingerprint, lineItemStartDate, copyDeliveryProfiles]);

  const toggleDuplicateAllAdGroups = () => {
    const adGroupRows = getValues(`${fieldNamePrefix}.campaigns`);
    adGroupRows.forEach((adGroupRow, index) => {
      setValue(`${fieldNamePrefix}.campaigns.${index}.copy`, !duplicateAllAdGroups);
    });
    setDuplicateAllAdGroups(!duplicateAllAdGroups);
  };

  const toggleDuplicateAllLocations = () => {
    const adGroupRows = getValues(`${fieldNamePrefix}.campaigns`);
    adGroupRows.forEach((adGroupRow, index) => {
      setValue(`${fieldNamePrefix}.campaigns.${index}.copyLocations`, !duplicateAllLocations);
    });
    setDuplicateAllLocations(!duplicateAllLocations);
  };

  const toggleDuplicateAllCreatives = () => {
    const adGroupRows = getValues(`${fieldNamePrefix}.campaigns`);
    adGroupRows.forEach((adGroupRow, index) => {
      setValue(`${fieldNamePrefix}.campaigns.${index}.copyTargetedCreatives`, !duplicateAllCreatives);
    });
    setDuplicateAllCreatives(!duplicateAllCreatives);
  };

  return (
    <Stack alignItems="flex-start" spacing={2} padding={2}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box p={2}>
          <FormControlLabel
            label="Show Original Line Item Details"
            control={<Switch onClick={() => setShowDetails(!isShowDetails)} />}
          />
          <Collapse in={isShowDetails} timeout="auto" unmountOnExit>
            <Box sx={{ height: '300px', overflowY: 'scroll' }}>
              <Stack alignItems="flex-start" spacing={1} py={2}>
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>{lineItem.lineItemName}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>State</TableCell>
                        <TableCell>{lineItem.uiState}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Start Date</TableCell>
                        <TableCell>{lineItem.startDate && format(lineItem.startDate, 'yyyy-MM-dd')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>End Date</TableCell>
                        <TableCell>{lineItem.endDate && format(lineItem.endDate, 'yyyy-MM-dd')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Non Revenue</TableCell>
                        <TableCell>{lineItem.nonRevenueEnabled}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Bid Frequency</TableCell>
                        <TableCell>{lineItem.bidFrequency}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Bid Priority</TableCell>
                        <TableCell>{lineItem.bidPriority}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Target Strategy</TableCell>
                        <TableCell>{lineItem.targetStrategy}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Impression Advisory Budget</TableCell>
                        <TableCell>{lineItem.advisoryBudget}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Impression Budget</TableCell>
                        <TableCell>{lineItem.impressionBudget}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Optimization Type</TableCell>
                        <TableCell>{lineItem.optimizationType}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Min CTR</TableCell>
                        <TableCell>{lineItem.minCtrOptimization}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Min Conversion Optimization</TableCell>
                        <TableCell>{lineItem.minConversionOptimization}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Daily Impression Limit</TableCell>
                        <TableCell>{lineItem.dailyImpressionLimit}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Intra-Day Spend Pacing</TableCell>
                        <TableCell>{lineItem.intradaySpendPacing}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Dynamic Spend Pacing</TableCell>
                        <TableCell>{lineItem.dynamicSpendPacing}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Pacing Strategies</TableCell>
                        <TableCell>{lineItem.pacingStrategies}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Box>
          </Collapse>
        </Box>
      </Paper>
      {/* Scoped to the standalone line item duplicate (ADOPS-2722). The contract clone
          reuses this component with a per-IO prefix but does not wire up the deselect
          overrides, so the warnings/controls are intentionally not shown there. */}
      {fieldNamePrefix === 'new_insertion_order' && <CloneFieldWarnings warnings={cloneWarnings} />}
      <TextField
        name={`${fieldNamePrefix}.name`}
        label="New Line Item Name"
        sx={{ width: '100%' }}
        rules={{ required: { value: true, message: 'Name is required' } }}
      />
      {fieldNamePrefix === 'new_insertion_order' && (
        <Grid container>
          <NumericTextField
            name="number_of_copies"
            label="Copies"
            allowNegative={false}
            decimalScale={0}
            isAllowed={(input) => isBlank(input.floatValue) || (input.floatValue >= 1 && input.floatValue <= 10)}
            sx={{ width: '25%', mt: 1 }}
            rules={{ required: { value: true, message: 'Copies is required' } }}
          />
        </Grid>
      )}
      <Typography>Flight Dates</Typography>
      <Stack direction="row" spacing={2}>
        <DatePicker
          label="Start Date"
          name={`${fieldNamePrefix}.start_date`}
          rules={{
            required: { value: true, message: 'Start date is required' },
          }}
        />
        <div>
          <Typography>To</Typography>
        </div>
        <DatePicker
          label="End Date"
          name={`${fieldNamePrefix}.end_date`}
          rules={{
            required: { value: true, message: 'End date is required' },
          }}
        />
      </Stack>
      <Typography>Budget</Typography>
      <Grid container>
        <Grid item xs={6}>
          <TextField
            name={`${fieldNamePrefix}.advisory_budget`}
            label="Advisory Budget"
            sx={{ width: '80%' }}
            rules={{
              required: { value: true, message: 'Advisory budget is required' },
              onChange: (e) => {
                trigger(`${fieldNamePrefix}.advisory_budget`);
                return e.target.value;
              },
              onBlur: (e) =>
                setValue(`${fieldNamePrefix}.advisory_budget`, round(e?.target?.value), {
                  shouldDirty: true,
                  shouldValidate: true,
                }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            name={`${fieldNamePrefix}.bonus`}
            label="Bonus"
            sx={{ width: '80%' }}
            rules={{
              onBlur: (e) =>
                setValue(`${fieldNamePrefix}.bonus`, omitCents(e?.target?.value), {
                  shouldDirty: true,
                  shouldValidate: true,
                }),
            }}
          />
        </Grid>
      </Grid>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <FormControlLabel
                  label=""
                  control={<InMarKitCheckbox checked={duplicateAllAdGroups} onChange={toggleDuplicateAllAdGroups} />}
                />
              </TableCell>
              <TableCell>Ad Group Name</TableCell>
              <TableCell>
                <FormControlLabel
                  label="Copy Locations"
                  control={
                    <InMarKitCheckbox checked={duplicateAllLocations} onChange={toggleDuplicateAllLocations} />
                  }
                />
              </TableCell>
              <TableCell>
                <FormControlLabel
                  label="Copy Targeted Creatives"
                  control={
                    <InMarKitCheckbox checked={duplicateAllCreatives} onChange={toggleDuplicateAllCreatives} />
                  }
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isEmpty(adGroups) &&
              Object.values(adGroups).map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell>
                    <Checkbox name={`${fieldNamePrefix}.campaigns.${index}.copy`} label="" />
                  </TableCell>
                  <TableCell>
                    <TextField
                      name={`${fieldNamePrefix}.campaigns.${index}.name`}
                      label="Ad Group Name"
                      sx={{ width: '100%' }}
                      rules={{ required: { value: true, message: 'Name is required' } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox name={`${fieldNamePrefix}.campaigns.${index}.copyLocations`} label="" />
                  </TableCell>
                  <TableCell>
                    <Checkbox name={`${fieldNamePrefix}.campaigns.${index}.copyTargetedCreatives`} label="" />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!isEmpty(deliveryProfiles) && (
        <Paper sx={{ width: '100%', mt: 2 }}>
          <Box p={2}>
            <Checkbox
              name={`${fieldNamePrefix}.copyDeliveryProfiles`}
              label="Copy Custom Delivery Profiles"
            />
            <Collapse in={copyDeliveryProfiles} timeout="auto" unmountOnExit>
              <TableContainer sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Impression Budget</TableCell>
                      <TableCell>Bonus</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliveryProfiles.map((field, index) => {
                      const row = watchedProfiles?.[index];
                      return (
                      <TableRow key={field.id}>
                        <TableCell>
                          <DatePicker
                            label=""
                            name={`${fieldNamePrefix}.customDeliveryProfiles.${index}.startDate`}
                            minDate={lineItemStartDate ?? undefined}
                            maxDate={row?.endDate ?? undefined}
                            shouldDisableDate={(d) => isDateTakenByOtherRow(index, d as Date)}
                            rules={{ validate: validateProfileStart(index) }}
                          />
                        </TableCell>
                        <TableCell>
                          <DatePicker
                            label=""
                            name={`${fieldNamePrefix}.customDeliveryProfiles.${index}.endDate`}
                            minDate={row?.startDate ?? lineItemStartDate ?? undefined}
                            shouldDisableDate={(d) => isDateTakenByOtherRow(index, d as Date)}
                            rules={{ validate: validateProfileEnd(index) }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            label=""
                            name={`${fieldNamePrefix}.customDeliveryProfiles.${index}.profileImpressionBudget`}
                            sx={{ width: '120px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            label=""
                            name={`${fieldNamePrefix}.customDeliveryProfiles.${index}.bonus`}
                            sx={{ width: '120px' }}
                          />
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Box>
        </Paper>
      )}
    </Stack>
  );
}

export default DuplicateLineItemFields;
